import { NotificationInfoOutlineIcon } from '@tableau/icons/NotificationInfoOutlineIcon';
import { ButtonType, ButtonWidget, DialogPositioningBehavior, DialogType, DialogWidget, RadioButtonWithLabelOption, TextLinkWidget, Tooltip } from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import React from 'react';

import { FormikTextBox } from '~src/commonInfra/components/FormikField';
import { FormikRadioButtonGroup } from '~src/commonInfra/components/FormikRadioButtonGroup';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { SelectModelsDialogStyles } from '~src/settings/general-settings/styles/tableau-gai/SelectModelsDialogStyles';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';

import { Messages } from '../../../../../codegen/Localize';

export interface SelectModelsOnlineDialogProps {
  onClose: () => void;
  onSubmit: (values: SelectModelsOnlineDialogState) => void;
  initialValues?: SelectModelsOnlineDialogState;
}

export interface SelectModelsOnlineDialogState {
  modelUnderstandUserRequest: string;
  modelVizAndCalcGeneration: string;
  modelProcessDataContext: string;
  modelIsDefaultOnlineModels: boolean;
}

function getButtonList(): RadioButtonWithLabelOption<boolean>[] {
  return [{
    testId: 'select-models-online-rb-defaultOnlineModels',
    label: Messages.selectModelsOnlineDialogDefaultModelsAzureOpenAiLabel(),
    value: true,
  },
  {
    testId: 'select-models-online-rb-customModels',
    label: Messages.selectModelsOnlineDialogCustomModelsLabel(),
    value: false,
  }];
}

function ModelUnderstandUserRequestsLabelSection(): JSX.Element {
  return (
    <tr>
      <td className='tb-text-input-label-cell tb-wide-overflow'>
        <div className={SelectModelsDialogStyles.labelWithTooltipContainer}>
          <label htmlFor='modelUnderstandUserRequest'>{Messages.selectModelsOnlineDialogUnderstandUserRequestsLabel()}</label>
          <Tooltip tooltipContent={Messages.selectModelsOnlineDialogUnderstandUserRequestsTooltip()}>
            <NotificationInfoOutlineIcon size={16} />
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}

function ModelUnderstandUserRequestsTextFieldSection(formik: FormikProps<SelectModelsOnlineDialogState>): JSX.Element {
  return (
    <tr>
      <td className='tb-settings-control tb-input-wide'>
        <FormikTextBox
          name='modelUnderstandUserRequest'
          testId='select-models-online-input-modelUnderstandUserRequest'
          label={''}
          placeholder={Messages.selectModelsOnlineDialogDefaultOption()}
          className={SelectModelsDialogStyles.dropdownStyle}
        />
      </td>
    </tr>
  );
}

function ModelVizAndCalcGenerationLabelSection(): JSX.Element {
  return (
    <tr>
      <td className='tb-text-input-label-cell tb-wide-overflow'>
        <div className={SelectModelsDialogStyles.labelWithTooltipContainer}>
          <label htmlFor='modelVizAndCalcGeneration'>{Messages.selectModelsOnlineDialogVizAndCalcGenerationLabel()}</label>
          <Tooltip tooltipContent={Messages.selectModelsOnlineDialogVizAndCalcGenerationTooltip()}>
            <NotificationInfoOutlineIcon size={16} />
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}

function ModelVizAndCalcGenerationTextFieldSection(formik: FormikProps<SelectModelsOnlineDialogState>): JSX.Element {
  return (
    <tr>
      <td className='tb-settings-control tb-input-wide'>
        <FormikTextBox
          name='modelVizAndCalcGeneration'
          testId='select-models-online-input-modelVizAndCalcGeneration'
          label={''}
          placeholder={Messages.selectModelsOnlineDialogDefaultOption()}
          className={SelectModelsDialogStyles.dropdownStyle}
        />
      </td>
    </tr>
  );
}

function ModelProcessDataContextLabelSection(): JSX.Element {
  return (
    <tr>
      <td className='tb-text-input-label-cell tb-wide-overflow'>
        <div className={SelectModelsDialogStyles.labelWithTooltipContainer}>
          <label htmlFor='modelProcessDataContext'>{Messages.selectModelsOnlineDialogProcessDataContextLabel()}</label>
          <Tooltip tooltipContent={Messages.selectModelsOnlineDialogProcessDataContextTooltip()}>
            <NotificationInfoOutlineIcon size={16} />
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}

function ModelProcessDataContextTextFieldSection(formik: FormikProps<SelectModelsOnlineDialogState>): JSX.Element {
  return (
    <tr>
      <td className='tb-settings-control tb-input-wide'>
        <FormikTextBox
          name='modelProcessDataContext'
          testId='select-models-online-input-modelProcessDataContext'
          label={''}
          placeholder={Messages.selectModelsOnlineDialogDefaultOption()}
          className={SelectModelsDialogStyles.dropdownStyle}
        />
      </td>
    </tr>
  );
}

export function SelectModelsOnlineDialog(props: SelectModelsOnlineDialogProps): JSX.Element {

  async function handleSubmit(modelsState: SelectModelsOnlineDialogState, formik: FormikHelpers<SelectModelsOnlineDialogState>) {
    formik.setSubmitting(true);
    props.onSubmit(modelsState);
    formik.setSubmitting(false);
    props.onClose();
  }

  const selectModelsLearnMoreLink = HelpService.getHelpUrlByCategory('gpt-server-models');
  const buttonList = getButtonList();

  return (
    <DialogWidget
      testId='select-models-online-dialog'
      type={DialogType.NormalWithTitleBar}
      title={Messages.selectModelsDialogTitle()}
      showCloseButton={true}
      onCloseRequest={props.onClose}
      handleGlassClick={props.onClose}
      positioningBehavior={DialogPositioningBehavior.Centered}
      isModal={true}
      shouldAdjustPositionOnWindowResize={true}
      dialogWidth={TabDialogWidth.width400px}
    >
      <Formik
        initialValues={props.initialValues}
        onSubmit={(values: SelectModelsOnlineDialogState, formik: FormikHelpers<SelectModelsOnlineDialogState>) => {
          void handleSubmit(values, formik);
        }}
      >
        {(formik: FormikProps<SelectModelsOnlineDialogState>) => (
          <form onSubmit={formik.handleSubmit} data-tb-test-id='select-models-online-dialog-form'>
            <div className={SelectModelsDialogStyles.descriptionContainer}>
              <p className={SelectModelsDialogStyles.descriptionText}>{Messages.selectModelsOnlineDialogDescription()}</p>
              <TextLinkWidget
                className={learnMoreStyle}
                href={selectModelsLearnMoreLink}
                target="_blank"
                testId={'select-models-online-learn-more'}
              >
                {Messages.learnMore()}
              </TextLinkWidget>
            </div>
            <FormikRadioButtonGroup name="modelIsDefaultOnlineModels" buttonList={buttonList} testId={'select-models-online-rb'} aria-label={Messages.selectModelsOnlineDialogModelSelectionAltText()}>
            </FormikRadioButtonGroup>

            {!formik.values.modelIsDefaultOnlineModels && (
              <table className={`tb-form-table ${SelectModelsDialogStyles.customLLMformTable}`}>
                <tbody>
                  {ModelUnderstandUserRequestsLabelSection()}
                  {ModelUnderstandUserRequestsTextFieldSection(formik)}
                  {ModelVizAndCalcGenerationLabelSection()}
                  {ModelVizAndCalcGenerationTextFieldSection(formik)}
                  {ModelProcessDataContextLabelSection()}
                  {ModelProcessDataContextTextFieldSection(formik)}
                </tbody>
              </table>
            )}
            <div className='tb-dialog-actions'>
              <ButtonWidget
                buttonType={ButtonType.Outline}
                testId='select-models-online-dialog-dismiss'
                onClick={() => props.onClose()}
                disabled={formik.isSubmitting}
                type='button'
              >
                {Messages.cancel()}
              </ButtonWidget>
              <ButtonWidget
                buttonType={ButtonType.Primary}
                testId='select-models-online-dialog-save-button'
                className='tb-margin-left-wide'
                disabled={
                  !formik.isValid ||
                  formik.isSubmitting ||
                  !formik.values.modelIsDefaultOnlineModels && (
                    !formik.values.modelUnderstandUserRequest ||
                    !formik.values.modelVizAndCalcGeneration ||
                    !formik.values.modelProcessDataContext ||
                    formik.values.modelUnderstandUserRequest === Messages.selectModelsOnlineDialogDefaultOption() ||
                    formik.values.modelVizAndCalcGeneration === Messages.selectModelsOnlineDialogDefaultOption() ||
                    formik.values.modelProcessDataContext === Messages.selectModelsOnlineDialogDefaultOption())
                }
                type='submit'
              >
                 {Messages.apply()}
              </ButtonWidget>
            </div>
          </form>
        )}
      </Formik>
    </DialogWidget>
  );
}

